import React, { useState, useEffect, lazy, Suspense } from 'react';
import { AccessibilityProvider } from './contexts/AccessibilityContext';
import { usePerformance } from './hooks/usePerformance';
import { PerformanceMonitor } from './components/PerformanceMonitor';
import { CookieConsentProvider } from './contexts/CookieConsentContext';
import { CookieConsentBanner } from './components/CookieConsentBanner';
import { PrivacyPage } from './components/PrivacyPage';

// Lazy load components for better performance
const AccessibilityMenu = lazy(() => import('./components/AccessibilityMenu').then(module => ({ default: module.AccessibilityMenu })));
const Navigation = lazy(() => import('./components/Navigation').then(module => ({ default: module.Navigation })));
const ProfileSection = lazy(() => import('./components/ProfileSection').then(module => ({ default: module.ProfileSection })));
const TeamSection = lazy(() => import('./components/TeamSection').then(module => ({ default: module.TeamSection })));
const ReferencesSection = lazy(() => import('./components/ReferencesSection').then(module => ({ default: module.ReferencesSection })));
const KontaktSection = lazy(() => import('./components/KontaktSection').then(module => ({ default: module.KontaktSection })));
const ImpressumSection = lazy(() => import('./components/ImpressumSection').then(module => ({ default: module.ImpressumSection })));
const Footer = lazy(() => import('./components/Footer').then(module => ({ default: module.Footer })));
const SkipToContent = lazy(() => import('./components/SkipToContent').then(module => ({ default: module.SkipToContent })));
const ReadingGuide = lazy(() => import('./components/ReadingGuide').then(module => ({ default: module.ReadingGuide })));

// Loading fallback component
const LoadingFallback = () => (
  <div className="flex items-center justify-center py-20">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-beige"></div>
  </div>
);

function App() {
  const [language, setLanguage] = useState<'de' | 'en'>('de');
  const [currentPage, setCurrentPage] = useState<'home' | 'privacy'>('home');
  const { markRenderStart } = usePerformance('App');

  // Set proper HTML lang attribute for screen readers and mark render start once
  useEffect(() => {
    markRenderStart();
    document.documentElement.lang = language;
  }, [language, markRenderStart]);

  // Handle navigation
  const showPrivacyPage = () => {
    setCurrentPage('privacy');
    // Scroll to top when opening privacy page
    window.scrollTo({ top: 0, left: 0 });
  };
  const showHomePage = () => {
    setCurrentPage('home');
    // Scroll to top when returning to home page
    window.scrollTo({ top: 0, left: 0 });
  };

  // Make navigation functions available globally
  useEffect(() => {
    (window as any).showPrivacyPage = showPrivacyPage;
  }, []);

  if (currentPage === 'privacy') {
    return (
      <CookieConsentProvider>
        <AccessibilityProvider>
          <PerformanceMonitor />
          <PrivacyPage language={language} onBack={showHomePage} />
        </AccessibilityProvider>
        <CookieConsentBanner language={language} onShowPrivacy={showPrivacyPage} />
      </CookieConsentProvider>
    );
  }

  return (
    <CookieConsentProvider>
      <AccessibilityProvider>
        <PerformanceMonitor />
        <div className="min-h-screen bg-white">
          <Suspense fallback={<LoadingFallback />}>
            <SkipToContent language={language} />
            <Navigation language={language} setLanguage={setLanguage} />
            <AccessibilityMenu language={language} />
            <ReadingGuide />
          </Suspense>
          
          <main id="main-content" className="pt-20 focus:outline-none" tabIndex={-1}>
            <Suspense fallback={<LoadingFallback />}>
              <ProfileSection language={language} />
            </Suspense>
            
            <Suspense fallback={<LoadingFallback />}>
              <TeamSection language={language} />
            </Suspense>
            
            <Suspense fallback={<LoadingFallback />}>
              <ReferencesSection language={language} />
            </Suspense>
            
            <Suspense fallback={<LoadingFallback />}>
              <KontaktSection language={language} />
            </Suspense>
            
            <Suspense fallback={<LoadingFallback />}>
              <ImpressumSection language={language} />
            </Suspense>
          </main>
          
          <Suspense fallback={<LoadingFallback />}>
            <Footer onShowPrivacy={showPrivacyPage} />
          </Suspense>
        </div>
      </AccessibilityProvider>
      <CookieConsentBanner language={language} onShowPrivacy={showPrivacyPage} />
    </CookieConsentProvider>
  );
}

export default App;
